import { useMemo, useState } from 'react';
import { useSites } from '../../../context/SiteContext';
import { useReportVisits } from '../../../hooks/useReportVisits';
import { formatDateTime } from '../../../lib/format';
import { classifySource, visitPagePath } from '../../../lib/source';
import { detectSpider, inferIsp, parseSearch } from '../../../lib/parse';
import EmptyState from '../../../components/dashboard/EmptyState';
import PageHeader from '../../../components/dashboard/PageHeader';
import StatTable from '../../../components/dashboard/StatTable';
import ExportButton from '../../../components/dashboard/ExportButton';

export default function LogsPage() {
  const { currentSite } = useSites();
  const { all, range } = useReportVisits(currentSite);
  const [keyword, setKeyword] = useState('');

  const columns = ['时间', 'IP', '地域', '运营商', '来源 / 搜索词', '受访页', '终端', '蜘蛛'];
  const rows = useMemo(() => {
    const filtered = range.bounds
      ? all.filter((v) => {
          const t = new Date(v.createdAt).getTime();
          return t >= range.bounds.from.getTime() && t < range.bounds.to.getTime();
        })
      : all;
    return filtered
      .filter((v) => {
        if (!keyword.trim()) return true;
        const q = keyword.trim().toLowerCase();
        return (
          v.ip.includes(q) ||
          v.pageUrl.toLowerCase().includes(q) ||
          v.city.includes(q) ||
          (v.referrer || '').toLowerCase().includes(q)
        );
      })
      .map((v) => {
        const search = parseSearch(v.referrer, v.pageUrl);
        return [
          formatDateTime(v.createdAt),
          v.ip,
          [v.province, v.city].filter(Boolean).join(' ') || v.city,
          inferIsp(v.ip, v.isp),
          search.keyword ? `${search.engine} / ${search.keyword}` : classifySource(v.referrer, v.pageUrl),
          visitPagePath(v.pageUrl),
          `${v.browser} / ${v.os}`,
          detectSpider(v.userAgent) || '—',
        ];
      });
  }, [all, range.bounds, keyword]);

  if (!currentSite) {
    return <EmptyState title="先添加一个站点" desc="添加站点后可查看完整访问明细。" />;
  }

  return (
    <div>
      <PageHeader
        title="访问明细"
        desc="逐条查看访问记录，支持按 IP、页面、地域筛选并导出。"
        range={range}
        extra={
          <div className="flex items-center gap-2">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="筛选 IP / 页面 / 城市"
              className="px-3 py-1.5 text-sm rounded-md border border-background-200"
            />
            <ExportButton filename={`${currentSite.domain}-logs.csv`} columns={columns} rows={rows} />
          </div>
        }
      />
      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <StatTable columns={columns} rows={rows} pageSize={20} empty="当前时段没有访问记录" />
      </div>
    </div>
  );
}
